import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BackupTableOutlinedIcon from '@mui/icons-material/BackupTableOutlined';
import EmojiTransportationOutlinedIcon from '@mui/icons-material/EmojiTransportationOutlined';
import LegendToggleOutlinedIcon from '@mui/icons-material/LegendToggleOutlined';
import SailingOutlinedIcon from '@mui/icons-material/SailingOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { Menu, Tooltip } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import {
  selectedMenuKeysSelector,
  selectedOpenKeysSelector,
} from 'features/app-info/selectors';
import {
  setSelectedMenuKeys,
  setSelectedOpenKeys,
} from 'features/app-info/slice';
import { DOOR_COST_REPORT_PAGE_TYPE } from 'features/summary-report/suppliers/door-cost/types';
import { useAppDispatch, useAppSelector } from 'lib/stores/hook';
import type { MenuInfo } from 'rc-menu/es/interface';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPath } from 'routes/router-paths';
import { v4 as uuid } from 'uuid';

const AppMainNavigationMenu = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const selectedMenuKeys = useAppSelector(selectedMenuKeysSelector);
  const selectedOpenKeys = useAppSelector(selectedOpenKeysSelector);

  const handleClickMenuItem = useCallback(
    (info: MenuInfo) => {
      const { key: navigationPath } = info;
      dispatch(setSelectedMenuKeys([navigationPath]));
      navigate(navigationPath);
    },
    [dispatch, navigate]
  );

  const renderSidebarLabel = (label: string) => {
    return (
      <Tooltip title={label} placement="right">
        {label}
      </Tooltip>
    );
  };

  const menuItems = useMemo(
    (): ItemType[] => [
      {
        key: 'Hệ thống',
        label: 'Hệ thống',
        type: 'group',
        children: [
          {
            key: uuid(),
            label: renderSidebarLabel('Quản trị hệ thống'),
            icon: <SettingsOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: getPath('roleManagementPage'),
                label: renderSidebarLabel('Phân quyền'),
              },
              {
                key: getPath('accessControlManagementPage'),
                label: renderSidebarLabel('Quản lý truy cập'),
              },
              {
                key: getPath('accountManagementPage'),
                label: renderSidebarLabel('Quản lý tài khoản'),
              },
            ],
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Danh mục'),
            icon: <BackupTableOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: uuid(),
                label: renderSidebarLabel('Hàng hóa'),
              },
            ],
          },
        ],
      },
      {
        key: 'Báo cáo tổng hợp',
        label: 'Báo cáo tổng hợp',
        type: 'group',
        children: [
          {
            key: uuid(),
            label: renderSidebarLabel('Báo cáo container'),
            icon: <AssessmentOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: uuid(),
                label: renderSidebarLabel('BC phiếu sửa container'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('Phiếu sửa container - BC nhanh'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('BC tổng hợp phiếu sửa container'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('Phiếu thu sửa chữa - BC nhanh'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('BC tỷ lệ sửa chữa container'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('Quản lý phiếu thu sửa chữa'),
              },
            ],
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Báo cáo nhà cung cấp'),
            icon: <LegendToggleOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: getPath(
                  'suppliersDoorCostSummaryReportPage',
                  DOOR_COST_REPORT_PAGE_TYPE.REVENUE
                ),
                label: renderSidebarLabel('Báo cáo chi phí door'),
              },
            ],
          },
        ],
      },
      {
        key: 'e-business',
        label: 'E-Business',
        type: 'group',
        children: [
          {
            key: uuid(),
            label: renderSidebarLabel('Quản lý container'),
            icon: <TableRowsOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: getPath('containerRepairOrdersPage'),
                label: renderSidebarLabel('Phiếu sửa container'),
              },
              {
                key: getPath('containerRepairQuotationSheetsPage'),
                label: renderSidebarLabel('Phiếu thu sửa chữa'),
              },
            ],
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Vận đơn điện tử - eBL'),
            icon: <SubtitlesOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: getPath('quotationListPage'),
                label: renderSidebarLabel('Quotation'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('Booking'),
              },
              {
                key: getPath('billOfLandingPage'),
                label: renderSidebarLabel('Bill of Lading'),
              },
            ],
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Quản lý nhà cung cấp'),
            icon: <EmojiTransportationOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: getPath('doorCostPage'),
                label: renderSidebarLabel('Chi phí door'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('Trucking và mua sắm'),
              },
            ],
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Quản lý thuyền viên'),
            icon: <SailingOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: getPath('crewMemberProfilesManagementPage'),
                label: renderSidebarLabel('Hồ sơ thuyền viên'),
              },
              {
                key: getPath('availableCrewMembersListPage'),
                label: renderSidebarLabel('Thuyền viên đang làm việc'),
              },
              {
                key: getPath('onLeaveCrewMembersListPage'),
                label: renderSidebarLabel('Thuyền viên nghỉ phép'),
              },
            ],
          },
          {
            key: uuid(),
            label: renderSidebarLabel('Quản lý khác'),
            icon: <TuneOutlinedIcon className="!text-base" />,
            type: 'submenu',
            children: [
              {
                key: uuid(),
                label: renderSidebarLabel('Quản lý giá nhân công'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('Quản lý hệ số'),
              },
              {
                key: uuid(),
                label: renderSidebarLabel('Quản lý free day'),
              },
            ],
          },
        ],
      },
    ],
    []
  );

  useEffect(() => {
    dispatch(setSelectedMenuKeys([location.pathname]));
  }, [dispatch, location.pathname]);

  return (
    <Menu
      mode="inline"
      items={menuItems}
      onClick={handleClickMenuItem}
      selectedKeys={selectedMenuKeys}
      openKeys={selectedOpenKeys}
      onOpenChange={keys => dispatch(setSelectedOpenKeys(keys))}
      className="!border-r-0"
    />
  );
};

export default memo(AppMainNavigationMenu);
